import { useState } from "react";
import { AlertTriangle, CalendarDays, CheckCircle2, Loader2, Send } from "lucide-react";
import {
  EVENT_TYPES,
  PACKAGE_OPTIONS,
  SERVICE_STYLE_OPTIONS,
  BUDGET_RANGES,
} from "../data/content";
import { submitQuoteToBtw } from "../lib/btwQuote";
import { Reveal } from "./Reveal";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const initialState = {
  fullName: "",
  organization: "",
  email: "",
  phone: "",
  eventDate: "",
  eventTime: "",
  eventLocation: "",
  guestCount: "",
  eventType: "",
  packageInterest: "",
  serviceStyle: "",
  budget: "",
  message: "",
  wantsMenu: false,
};

const validate = (v) => {
  const e = {};
  if (!v.fullName.trim()) e.fullName = "Full name is required";
  if (!v.email.trim()) e.email = "Email is required";
  else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.email)) e.email = "Enter a valid email";
  if (!v.phone.trim()) e.phone = "Phone number is required";
  else if (v.phone.replace(/\D/g, "").length < 10) e.phone = "Enter a valid phone number";
  if (!v.eventDate) e.eventDate = "Event date is required";
  if (!v.guestCount.trim()) e.guestCount = "Guest count is required";
  else if (Number(v.guestCount) <= 0) e.guestCount = "Enter a valid number";
  if (!v.eventType) e.eventType = "Select an event type";
  if (!v.packageInterest) e.packageInterest = "Select a package";
  if (!v.serviceStyle) e.serviceStyle = "Select a service style";
  return e;
};

export const QuoteForm = () => {
  const [values, setValues] = useState(initialState);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [referenceId, setReferenceId] = useState("");

  const update = (key) => (e) => {
    const val = e?.target ? (e.target.type === "checkbox" ? e.target.checked : e.target.value) : e;
    setValues((s) => ({ ...s, [key]: val }));
    if (errors[key]) setErrors((s) => ({ ...s, [key]: undefined }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitError("");
    const errs = validate(values);
    setErrors(errs);
    if (Object.keys(errs).length) {
      const firstKey = Object.keys(errs)[0];
      const el = document.querySelector(`[data-field='${firstKey}']`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    setSubmitting(true);
    const result = await submitQuoteToBtw(values);
    setSubmitting(false);
    if (result.ok) {
      const refId = result.data?.quote_token || result.data?.id || "";
      setReferenceId(refId);
      setSubmitted(true);
      // Fire-and-forget transactional emails (customer ack + internal notify).
      // Never block UX on email failure.
      try {
        const apiBase = process.env.REACT_APP_BACKEND_URL || "";
        fetch(`${apiBase}/api/quote/notify`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            name: values.fullName,
            email: values.email,
            phone: values.phone,
            organization: values.organization || null,
            event_date: values.eventDate || null,
            event_time: values.eventTime || null,
            event_type: values.eventType || null,
            event_location: values.eventLocation || null,
            guests: values.guestCount ? String(values.guestCount) : null,
            service_style: values.serviceStyle || null,
            package_interest: values.packageInterest || null,
            budget: values.budget || null,
            wants_menu: !!values.wantsMenu,
            message: values.message || null,
            reference_id: refId || null,
          }),
        }).catch((err) => {
          // Best-effort side channel — quote already saved server-side.
          // eslint-disable-next-line no-console
          console.warn("[quote-form] silent email notify skipped:", err?.message || err);
        });
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn("[quote-form] silent email notify failed:", err?.message || err);
      }
    } else {
      setSubmitError(
        result.error
          ? `We couldn't submit your request: ${result.error}. Please try again or call us directly.`
          : "We couldn't submit your request. Please try again or call us directly."
      );
    }
  };

  const reset = () => {
    setValues(initialState);
    setErrors({});
    setSubmitted(false);
    setSubmitError("");
    setReferenceId("");
  };

  return (
    <section
      id="quote"
      data-testid="quote-section"
      className="py-10 sm:py-14 lg:py-18 bg-brand-cream"
    >
      <div className="max-w-5xl mx-auto px-5 sm:px-6 lg:px-10">
        <div className="text-center max-w-2xl mx-auto">
          <Reveal>
            <p className="text-[12px] font-bold uppercase tracking-[0.22em] text-brand-teal">
              Request a Quote
            </p>
          </Reveal>
          <Reveal delay={1}>
            <h2 className="mt-3 font-display font-black text-3xl sm:text-4xl lg:text-5xl tracking-tight text-brand-charcoal leading-[1.05]">
              Request a Catering Quote.
            </h2>
          </Reveal>
          <Reveal delay={2}>
            <p className="mt-4 text-[15px] text-brand-charcoal/70 leading-relaxed">
              Share a few details and a member of the Fresh Fast Foods team will follow up
              with a custom quote.
            </p>
          </Reveal>
        </div>

        <Reveal delay={2}>
          <div className="mt-12 rounded-3xl bg-white border border-black/5 shadow-[0_24px_60px_-30px_rgba(17,17,17,0.25)] p-6 sm:p-10">
            {submitted ? (
              <div
                data-testid="quote-success"
                className="flex flex-col items-center text-center py-10"
              >
                <span className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-brand-green/15 text-brand-green">
                  <CheckCircle2 className="h-8 w-8" />
                </span>
                <h3 className="mt-6 font-display font-black text-2xl sm:text-3xl text-brand-charcoal tracking-tight">
                  Thank you. Your request has been received.
                </h3>
                <p className="mt-3 max-w-md text-[15px] text-brand-charcoal/70 leading-relaxed">
                  A member of the Fresh Fast Foods team will follow up with you shortly.
                  Keep an eye on your email and phone.
                </p>
                {referenceId ? (
                  <p
                    data-testid="quote-reference-id"
                    className="mt-4 text-[11px] font-bold uppercase tracking-[0.18em] text-brand-charcoal/50"
                  >
                    Reference: {referenceId}
                  </p>
                ) : null}
                <button
                  type="button"
                  data-testid="quote-submit-another-btn"
                  onClick={reset}
                  className="mt-8 inline-flex items-center gap-2 rounded-full bg-brand-charcoal text-white px-6 py-3 text-[12px] font-bold uppercase tracking-[0.12em] hover:bg-brand-teal transition-colors"
                >
                  Submit Another Request
                </button>
              </div>
            ) : (
              <form
                onSubmit={onSubmit}
                noValidate
                data-testid="quote-form"
                className="grid grid-cols-1 md:grid-cols-2 gap-5"
              >
                <Field label="Full Name *" error={errors.fullName} name="fullName">
                  <Input
                    data-testid="form-full-name"
                    value={values.fullName}
                    onChange={update("fullName")}
                    placeholder="Jane Doe"
                    className="h-12 rounded-xl bg-white border-black/10 focus-visible:ring-2 focus-visible:ring-brand-teal"
                  />
                </Field>
                <Field label="Organization / Company" name="organization">
                  <Input
                    data-testid="form-organization"
                    value={values.organization}
                    onChange={update("organization")}
                    placeholder="Optional"
                    className="h-12 rounded-xl bg-white border-black/10 focus-visible:ring-2 focus-visible:ring-brand-teal"
                  />
                </Field>
                <Field label="Email *" error={errors.email} name="email">
                  <Input
                    data-testid="form-email"
                    type="email"
                    value={values.email}
                    onChange={update("email")}
                    placeholder="you@company.com"
                    className="h-12 rounded-xl bg-white border-black/10 focus-visible:ring-2 focus-visible:ring-brand-teal"
                  />
                </Field>
                <Field label="Phone Number *" error={errors.phone} name="phone">
                  <Input
                    data-testid="form-phone"
                    type="tel"
                    value={values.phone}
                    onChange={update("phone")}
                    placeholder="(212) 555-0123"
                    className="h-12 rounded-xl bg-white border-black/10 focus-visible:ring-2 focus-visible:ring-brand-teal"
                  />
                </Field>
                <Field label="Event Date *" error={errors.eventDate} name="eventDate">
                  <div className="relative">
                    <CalendarDays className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-brand-charcoal/40 pointer-events-none" />
                    <Input
                      data-testid="form-event-date"
                      type="date"
                      value={values.eventDate}
                      onChange={update("eventDate")}
                      className="h-12 pl-10 rounded-xl bg-white border-black/10 focus-visible:ring-2 focus-visible:ring-brand-teal"
                    />
                  </div>
                </Field>
                <Field label="Event Time" name="eventTime">
                  <Input
                    data-testid="form-event-time"
                    type="time"
                    value={values.eventTime}
                    onChange={update("eventTime")}
                    className="h-12 rounded-xl bg-white border-black/10 focus-visible:ring-2 focus-visible:ring-brand-teal"
                  />
                </Field>
                <Field label="Event Location" name="eventLocation" className="md:col-span-2">
                  <Input
                    data-testid="form-event-location"
                    value={values.eventLocation}
                    onChange={update("eventLocation")}
                    placeholder="Address or borough"
                    className="h-12 rounded-xl bg-white border-black/10 focus-visible:ring-2 focus-visible:ring-brand-teal"
                  />
                </Field>
                <Field label="Guest Count *" error={errors.guestCount} name="guestCount">
                  <Input
                    data-testid="form-guest-count"
                    type="number"
                    min={1}
                    value={values.guestCount}
                    onChange={update("guestCount")}
                    placeholder="e.g. 50"
                    className="h-12 rounded-xl bg-white border-black/10 focus-visible:ring-2 focus-visible:ring-brand-teal"
                  />
                </Field>
                <Field label="Event Type *" error={errors.eventType} name="eventType">
                  <Select value={values.eventType} onValueChange={update("eventType")}>
                    <SelectTrigger
                      data-testid="form-event-type"
                      className="h-12 rounded-xl bg-white border-black/10 focus:ring-2 focus:ring-brand-teal"
                    >
                      <SelectValue placeholder="Select event type" />
                    </SelectTrigger>
                    <SelectContent>
                      {EVENT_TYPES.map((t) => (
                        <SelectItem key={t} value={t}>
                          {t}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>

                <Field label="Package Interest *" error={errors.packageInterest} name="packageInterest">
                  <Select value={values.packageInterest} onValueChange={update("packageInterest")}>
                    <SelectTrigger
                      data-testid="form-package-interest"
                      className="h-12 rounded-xl bg-white border-black/10 focus:ring-2 focus:ring-brand-teal"
                    >
                      <SelectValue placeholder="Choose a package" />
                    </SelectTrigger>
                    <SelectContent>
                      {PACKAGE_OPTIONS.map((t) => (
                        <SelectItem key={t} value={t}>
                          {t}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>

                <Field label="Service Style *" error={errors.serviceStyle} name="serviceStyle">
                  <Select value={values.serviceStyle} onValueChange={update("serviceStyle")}>
                    <SelectTrigger
                      data-testid="form-service-style"
                      className="h-12 rounded-xl bg-white border-black/10 focus:ring-2 focus:ring-brand-teal"
                    >
                      <SelectValue placeholder="Choose a service style" />
                    </SelectTrigger>
                    <SelectContent>
                      {SERVICE_STYLE_OPTIONS.map((t) => (
                        <SelectItem key={t} value={t}>
                          {t}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>

                <Field label="Budget Range" name="budget">
                  <Select value={values.budget} onValueChange={update("budget")}>
                    <SelectTrigger
                      data-testid="form-budget"
                      className="h-12 rounded-xl bg-white border-black/10 focus:ring-2 focus:ring-brand-teal"
                    >
                      <SelectValue placeholder="Optional" />
                    </SelectTrigger>
                    <SelectContent>
                      {BUDGET_RANGES.map((t) => (
                        <SelectItem key={t} value={t}>
                          {t}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>

                <Field label="Message / Special Requests" name="message" className="md:col-span-2">
                  <Textarea
                    data-testid="form-message"
                    value={values.message}
                    onChange={update("message")}
                    placeholder="Allergies, dietary needs, setup notes…"
                    rows={4}
                    className="rounded-xl bg-white border-black/10 focus-visible:ring-2 focus-visible:ring-brand-teal"
                  />
                </Field>

                <div className="md:col-span-2 flex items-center gap-3">
                  <Checkbox
                    data-testid="form-wants-menu"
                    id="wantsMenu"
                    checked={values.wantsMenu}
                    onCheckedChange={(c) => setValues((s) => ({ ...s, wantsMenu: !!c }))}
                    className="border-black/30 data-[state=checked]:bg-brand-teal data-[state=checked]:border-brand-teal"
                  />
                  <Label
                    htmlFor="wantsMenu"
                    className="text-[14px] font-medium text-brand-charcoal/80"
                  >
                    I am interested in receiving the catering menu.
                  </Label>
                </div>

                <div className="md:col-span-2 flex flex-col sm:flex-row items-stretch sm:items-center gap-3 pt-2">
                  {submitError ? (
                    <div
                      data-testid="quote-submit-error"
                      role="alert"
                      className="md:col-span-2 w-full inline-flex items-start gap-3 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-[13px] text-red-700"
                    >
                      <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                      <span className="leading-snug">{submitError}</span>
                    </div>
                  ) : null}
                </div>

                <div className="md:col-span-2 flex flex-col sm:flex-row items-stretch sm:items-center gap-3 pt-2">
                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="form-submit-btn"
                    className="inline-flex items-center justify-center gap-2 rounded-full bg-brand-charcoal text-white px-7 py-4 text-[13px] font-bold uppercase tracking-[0.12em] hover:bg-brand-teal transition-all hover:scale-[1.02] disabled:opacity-60 disabled:cursor-not-allowed shadow-[0_18px_40px_-18px_rgba(17,17,17,0.5)]"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Sending…
                      </>
                    ) : (
                      <>
                        Send Catering Request
                        <Send className="h-4 w-4" />
                      </>
                    )}
                  </button>
                  <p className="text-[12px] text-brand-charcoal/60 leading-snug">
                    By submitting, you agree to be contacted by the Fresh Fast Foods team
                    about your catering request.
                  </p>
                </div>
              </form>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
};

const Field = ({ label, error, name, children, className = "" }) => (
  <div data-field={name} className={`flex flex-col gap-1.5 ${className}`}>
    <Label className="text-[12px] font-bold uppercase tracking-[0.14em] text-brand-charcoal/70">
      {label}
    </Label>
    {children}
    {error && (
      <p
        className="text-[12px] text-red-600 font-medium"
        data-testid={`error-${name}`}
        role="alert"
      >
        {error}
      </p>
    )}
  </div>
);

export default QuoteForm;
